import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { authFetchJson } from '../lib/authFetch'

// Matches the actual GET /api/v2/organizations/:id response (returned flat, NOT wrapped)
interface OrgPerson {
  id: number
  person_id: number
  name: string
  email: string | null
  title: string | null
  affiliation_role: string | null
  affiliation_title: string | null
  start_date: string | null
  end_date: string | null
  is_primary: boolean | number | null
}

interface OrgProject {
  id: number
  title: string
  org_role: string
}

interface OrgDetail {
  id: number
  name: string
  legal_name: string | null
  type: string
  country: string | null
  city: string | null
  website: string | null
  email_domain: string | null
  logo_url: string | null
  description: string | null
  tags: string[] | null
  created_at: string | null
  updated_at: string | null
  people: OrgPerson[]
  projects: OrgProject[]
}

const orgTypeLabels: Record<string, string> = {
  university: 'University',
  research_institute: 'Research Institute',
  corporate: 'Corporate',
  startup: 'Startup',
  vc: 'Venture Capital',
  government: 'Government',
  ngo: 'NGO',
  other: 'Other',
}

const orgTypeOptions = [
  'university',
  'research_institute',
  'corporate',
  'startup',
  'vc',
  'government',
  'ngo',
  'other',
]

type EditField =
  | 'name'
  | 'legal_name'
  | 'type'
  | 'country'
  | 'city'
  | 'website'
  | 'description'
  | null

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [org, setOrg] = useState<OrgDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingField, setEditingField] = useState<EditField>(null)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Load org details — API returns flat object (not wrapped)
  const fetchOrg = useCallback(async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const data = await authFetchJson<OrgDetail>(`/api/v2/organizations/${id}`)
      setOrg(data)
      setError(null)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load organization'
      )
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchOrg()
  }, [fetchOrg])

  const handleStartEdit = (field: EditField, value: string) => {
    setEditingField(field)
    setEditValue(value || '')
  }

  const handleSaveEdit = async (field: EditField) => {
    if (!org || !field) return
    try {
      setIsSaving(true)
      setError(null)
      await authFetchJson(`/api/v2/organizations/${org.id}`, {
        method: 'PATCH',
        json: { [field]: editValue || null },
      })
      // Re-fetch full detail
      await fetchOrg()
      setEditingField(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingField(null)
    setEditValue('')
    setError(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
          <span className="text-body text-text-secondary">Loading...</span>
        </div>
      </div>
    )
  }

  if (!org) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-body text-red-400 mb-4">
            {error || 'Organization not found'}
          </p>
          <button
            onClick={() => navigate('/organizations')}
            className="px-4 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Organizations
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/organizations')}
          className="text-primary-red hover:text-red-700 mb-4 text-sm font-medium"
        >
          &larr; Back to Organizations
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-h1 text-text-primary">{org.name}</h1>
          <span className="inline-block px-2.5 py-1 bg-bg-surface border border-border-subtle rounded-full text-xs text-text-secondary">
            {orgTypeLabels[org.type] || org.type}
          </span>
        </div>
        {org.city && (
          <p className="text-body text-text-secondary mt-1">
            {org.city}
            {org.country ? `, ${org.country}` : ''}
          </p>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-400/10 border border-red-400/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Organization Details */}
      <div className="rounded-lg bg-bg-surface border border-border-subtle p-6 mb-6">
        <h2 className="text-h2 text-text-primary mb-4">Organization Details</h2>
        <div className="space-y-4">
          {/* Name */}
          <EditableRow
            label="Name"
            value={org.name}
            field="name"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />

          {/* Legal Name */}
          <EditableRow
            label="Legal Name"
            value={org.legal_name}
            field="legal_name"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />

          {/* Type */}
          <div>
            <label className="text-xs font-medium text-text-tertiary uppercase">
              Type
            </label>
            {editingField === 'type' ? (
              <div className="flex gap-2 mt-1">
                <select
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                >
                  {orgTypeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {orgTypeLabels[opt]}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => handleSaveEdit('type')}
                  disabled={isSaving}
                  className="px-3 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-2 bg-bg-body border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <p
                onClick={() => handleStartEdit('type', org.type)}
                className="mt-1 text-body text-text-secondary cursor-pointer hover:text-primary-red transition-colors"
              >
                {orgTypeLabels[org.type] || org.type}
              </p>
            )}
          </div>

          {/* Country */}
          <EditableRow
            label="Country"
            value={org.country}
            field="country"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />

          {/* City */}
          <EditableRow
            label="City"
            value={org.city}
            field="city"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />

          {/* Website */}
          <div>
            <label className="text-xs font-medium text-text-tertiary uppercase">
              Website
            </label>
            {editingField === 'website' ? (
              <div className="flex gap-2 mt-1">
                <input
                  type="url"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                />
                <button
                  onClick={() => handleSaveEdit('website')}
                  disabled={isSaving}
                  className="px-3 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-2 bg-bg-body border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <p
                onClick={() => handleStartEdit('website', org.website || '')}
                className="mt-1 text-body text-text-secondary cursor-pointer hover:text-primary-red transition-colors"
              >
                {org.website ? (
                  <a
                    href={org.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-red hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {org.website}
                  </a>
                ) : (
                  '\u2014'
                )}
              </p>
            )}
          </div>

          {/* Description */}
          <EditableRow
            label="Description"
            value={org.description}
            field="description"
            multiline
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />
        </div>
      </div>

      {/* Affiliated People */}
      <div className="rounded-lg bg-bg-surface border border-border-subtle p-6 mb-6">
        <h2 className="text-h2 text-text-primary mb-4">
          Affiliated People
          {org.people.length > 0 && (
            <span className="ml-2 text-sm font-normal text-text-tertiary">
              ({org.people.length})
            </span>
          )}
        </h2>
        {org.people.length > 0 ? (
          <div className="space-y-2">
            {org.people.map((person) => (
              <Link
                key={`${person.person_id}-${person.affiliation_role}`}
                to={`/people/${person.person_id}`}
                className="flex justify-between items-start p-4 rounded-lg bg-bg-body border border-border-subtle hover:border-primary-red transition-colors block"
              >
                <div>
                  <p className="text-body font-medium text-text-primary">
                    {person.name}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {person.affiliation_role || 'Member'}
                    {person.affiliation_title &&
                      ` \u2022 ${person.affiliation_title}`}
                  </p>
                  {person.email && (
                    <p className="text-xs text-text-tertiary mt-1">
                      {person.email}
                    </p>
                  )}
                </div>
                <span className="text-primary-red text-sm">&rarr;</span>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-tertiary">
            No people affiliated with this organization
          </p>
        )}
      </div>

      {/* Projects */}
      <div className="rounded-lg bg-bg-surface border border-border-subtle p-6">
        <h2 className="text-h2 text-text-primary mb-4">
          Linked Projects
          {org.projects.length > 0 && (
            <span className="ml-2 text-sm font-normal text-text-tertiary">
              ({org.projects.length})
            </span>
          )}
        </h2>
        {org.projects.length > 0 ? (
          <div className="space-y-2">
            {org.projects.map((proj) => (
              <Link
                key={proj.id}
                to={`/projects/${proj.id}`}
                className="flex justify-between items-start p-4 rounded-lg bg-bg-body border border-border-subtle hover:border-primary-red transition-colors block"
              >
                <div>
                  <p className="text-body font-medium text-text-primary">
                    {proj.title}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {proj.org_role}
                  </p>
                </div>
                <span className="text-primary-red text-sm">&rarr;</span>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-tertiary">
            No projects linked to this organization
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Reusable inline-edit row ───────────────────────────────────────────────

interface EditableRowProps {
  label: string
  value: string | null | undefined
  field: EditField
  multiline?: boolean
  editingField: EditField
  editValue: string
  isSaving: boolean
  onStartEdit: (field: EditField, value: string) => void
  onSaveEdit: (field: EditField) => void
  onCancelEdit: () => void
  onEditValueChange: (value: string) => void
}

function EditableRow({
  label,
  value,
  field,
  multiline,
  editingField,
  editValue,
  isSaving,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditValueChange,
}: EditableRowProps) {
  const isEditing = editingField === field

  return (
    <div>
      <label className="text-xs font-medium text-text-tertiary uppercase">
        {label}
      </label>
      {isEditing ? (
        <div className="flex gap-2 mt-1">
          {multiline ? (
            <textarea
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              rows={3}
              className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
            />
          ) : (
            <input
              type="text"
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
            />
          )}
          <div className={multiline ? 'flex flex-col gap-2' : 'flex gap-2'}>
            <button
              onClick={() => onSaveEdit(field)}
              disabled={isSaving}
              className="px-3 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
            >
              Save
            </button>
            <button
              onClick={onCancelEdit}
              className="px-3 py-2 bg-bg-body border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p
          onClick={() => onStartEdit(field, value || '')}
          className={`mt-1 text-body cursor-pointer hover:text-primary-red transition-colors ${
            multiline ? 'whitespace-pre-wrap' : ''
          } ${value ? 'text-text-primary' : 'text-text-tertiary'}`}
        >
          {value || '\u2014'}
        </p>
      )}
    </div>
  )
}
